import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authAPI } from '../services/api';

type VerifyState = 'loading' | 'verified' | 'used' | 'expired' | 'invalid';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [state, setState] = useState<VerifyState>('loading');
  const [message, setMessage] = useState<string>('');
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [showPostVerifyModal, setShowPostVerifyModal] = useState(false);
  const [affiliatePaymentRequired, setAffiliatePaymentRequired] = useState(false);
  const [affiliatePaymentUrl, setAffiliatePaymentUrl] = useState<string | null>(null);
  const [affiliatePaymentMessage, setAffiliatePaymentMessage] = useState<string | null>(null);
  const [redirectingToPayment, setRedirectingToPayment] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setState('invalid');
        return;
      }
      try {
        const response = await authAPI.verifyEmail(token);
        const affiliateRequired = Boolean((response as any).affiliate_payment_required);
        const paymentUrl = (response as any).payment_url || null;
        const paymentMessage = (response as any).payment_message || 'Complete payment to activate your account login.';

        if (affiliateRequired) {
          setAffiliatePaymentRequired(true);
          setAffiliatePaymentUrl(paymentUrl);
          setAffiliatePaymentMessage(paymentMessage);
        } else {
          setAffiliatePaymentRequired(false);
          setAffiliatePaymentUrl(null);
          setAffiliatePaymentMessage(null);
        }

        if (response.status === 'verified') {
          setState('verified');
          if (affiliateRequired) {
            setShowPostVerifyModal(true);
            if (paymentUrl) {
              setRedirectingToPayment(true);
              window.location.assign(paymentUrl);
              return;
            }
          } else {
            setShowPostVerifyModal(true);
          }
        } else if (response.status === 'used') {
          setState('used');
          if (affiliateRequired) {
            setShowPostVerifyModal(true);
            if (paymentUrl) {
              setRedirectingToPayment(true);
              window.location.assign(paymentUrl);
              return;
            }
          }
        } else if (response.status === 'expired') {
          setState('expired');
        } else {
          setState('invalid');
        }
        if (response.message) {
          setMessage(response.message);
        }
      } catch (err: any) {
        const status = err?.response?.data?.status;
        if (status === 'used') {
          setState('used');
        } else if (status === 'expired') {
          setState('expired');
        } else {
          setState('invalid');
        }
        if (err?.response?.data?.message) {
          setMessage(err.response.data.message);
        }
      }
    };

    verifyToken();
  }, [token]);

  if (state === 'loading') {
    return (
      <div className="container center">
        <div className="card">
          <p className="muted">Verifying your email...</p>
        </div>
      </div>
    );
  }

  const title = state === 'verified' || state === 'used'
    ? 'Email verified'
    : 'Verification link unavailable';

  const detail = message || (
    state === 'verified'
      ? 'Email has been successfully verified. You can now sign in.'
      : state === 'used'
        ? 'This verification link has already been verified. You can now sign in.'
        : state === 'expired'
          ? 'This verification link has expired.'
          : 'This verification link is invalid.'
  );

  const handleResend = async () => {
    if (!email.trim()) {
      setResendError('Please enter your email address.');
      return;
    }
    setSending(true);
    setResendMessage(null);
    setResendError(null);
    try {
      const payload: { email: string; new_email?: string } = { email: email.trim() };
      if (newEmail.trim() && newEmail.trim().toLowerCase() !== email.trim().toLowerCase()) {
        payload.new_email = newEmail.trim();
      }
      const response = await authAPI.resendVerificationEmail(payload);
      if (response.email) {
        setEmail(response.email);
        setNewEmail('');
      }
      if (typeof response.remaining_attempts === 'number') {
        setRemainingAttempts(response.remaining_attempts);
        if (response.remaining_attempts === 0) {
          const retryAfter = response.retry_after_seconds || 24 * 3600;
          setCooldownUntil(Date.now() + retryAfter * 1000);
        }
      }
      setResendMessage(response.message || 'Verification email resent.');
    } catch (err: any) {
      const retryAfter = err?.response?.data?.retry_after_seconds;
      if (retryAfter) {
        setCooldownUntil(Date.now() + retryAfter * 1000);
      }
      setResendError(err?.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setSending(false);
    }
  };

  const resendDisabled = sending || (cooldownUntil !== null && Date.now() < cooldownUntil);

  return (
    <div className="container center">
      <div className="card">
        <h2 className="h2">{title}</h2>
        {(state === 'verified' || state === 'used') && (
          <div className="alert success">Email has been successfully verified.</div>
        )}
        <p className="muted">{detail}</p>
        {state !== 'verified' && state !== 'used' && (
          <>
            {resendMessage && <div className="alert success">{resendMessage}</div>}
            {resendError && <div className="alert error">{resendError}</div>}
            {typeof remainingAttempts === 'number' && remainingAttempts >= 0 && (
              <div className="muted" style={{ marginTop: '8px' }}>
                Remaining resend attempts today: {remainingAttempts}
              </div>
            )}
            {remainingAttempts === 0 && (
              <div className="muted" style={{ marginTop: '4px' }}>
                Please wait before trying again.
              </div>
            )}
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label className="label">Email address</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              />
            </div>
            <div className="form-group">
              <label className="label">New email (optional)</label>
              <input
                type="email"
                className="input"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter a corrected email"
              />
            </div>
            <button type="button" className="btn" onClick={handleResend} disabled={resendDisabled}>
              {resendDisabled ? 'Resend disabled (limit reached)' : sending ? 'Sending...' : 'Resend verification email'}
            </button>
          </>
        )}
        <Link to="/login" className="btn" style={{ marginTop: '12px' }}>
          Back to login
        </Link>
      </div>
      {showPostVerifyModal && (
        <div className="modal-overlay" onClick={() => setShowPostVerifyModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Registration Successful</h3>
              <button className="modal-close" onClick={() => setShowPostVerifyModal(false)} aria-label="Close">&times;</button>
            </div>
            <div className="modal-body">
              {affiliatePaymentRequired ? (
                <>
                  <p className="muted">
                    {redirectingToPayment
                      ? 'Redirecting you to payment...'
                      : (affiliatePaymentMessage || 'Your account requires affiliate payment completion before login.')}
                  </p>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button
                      className="btn"
                      onClick={() => {
                        if (affiliatePaymentUrl) {
                          window.location.assign(affiliatePaymentUrl);
                        }
                      }}
                      disabled={!affiliatePaymentUrl}
                    >
                      Pay Now
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="muted">
                    Your registration is complete. You can subscribe now or continue with the free subscription.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button
                      className="btn"
                      onClick={() => {
                        setShowPostVerifyModal(false);
                        navigate('/pricing');
                      }}
                    >
                      Subscribe
                    </button>
                    <button
                      className="btn ghost"
                      onClick={() => {
                        setShowPostVerifyModal(false);
                        navigate('/login');
                      }}
                    >
                      Continue Free
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifyEmail;
